/**
 * PistonSystem — Handles piston extension/retraction.
 *
 * When a piston receives redstone power:
 *   - Extends: pushes up to 12 blocks in front of it
 *   - Places PISTON_HEAD at the extension position
 *   - Moves all blocks in the push line forward by 1
 *
 * When power is removed:
 *   - Retracts: removes PISTON_HEAD
 *   - Sticky pistons pull the block in front back
 *
 * Pistons face the direction the player was looking when placed.
 * Direction stored in TileEntities as { type: 'piston', facing: 0-5 }
 * Facing: 0=+Y(up), 1=-Y(down), 2=+Z(south), 3=-Z(north), 4=+X(east), 5=-X(west)
 */
import { BlockId } from './BlockType.js';

const MAX_PUSH_BLOCKS = 12;

// Direction offsets for each facing value
const FACING_OFFSETS = [
  { x: 0, y: 1, z: 0 },   // 0: up
  { x: 0, y: -1, z: 0 },  // 1: down
  { x: 0, y: 0, z: 1 },   // 2: south (+Z)
  { x: 0, y: 0, z: -1 },  // 3: north (-Z)
  { x: 1, y: 0, z: 0 },   // 4: east (+X)
  { x: -1, y: 0, z: 0 },  // 5: west (-X)
];

// Blocks that cannot be pushed
const IMMOVABLE = new Set([
  BlockId.BEDROCK, BlockId.OBSIDIAN, BlockId.END_PORTAL_FRAME,
  BlockId.END_PORTAL, BlockId.NETHER_PORTAL,
]);

export class PistonSystem {
  constructor(world, tileEntities) {
    this.world = world;
    this.tileEntities = tileEntities;
    this._extendedPistons = new Set(); // "x,y,z" keys of currently extended pistons
  }

  /**
   * Get the facing direction for a piston at position.
   */
  getFacing(x, y, z) {
    const key = `${x},${y},${z}`;
    const te = this.tileEntities.data.get(key);
    return (te && te.facing !== undefined) ? te.facing : 0;
  }

  /**
   * Store piston facing when placed.
   * @param {number} facing - 0-5 direction index
   */
  setPistonFacing(x, y, z, facing) {
    const key = `${x},${y},${z}`;
    const block = this.world.getBlock(x, y, z);
    const isSticky = block === BlockId.STICKY_PISTON;
    this.tileEntities.data.set(key, { type: 'piston', facing, sticky: isSticky });
  }

  /**
   * Called when a piston receives or loses redstone power.
   * @param {boolean} powered - true = extend, false = retract
   */
  onPowerChange(x, y, z, powered) {
    const key = `${x},${y},${z}`;
    const isExtended = this._extendedPistons.has(key);

    if (powered && !isExtended) {
      this._extend(x, y, z);
    } else if (!powered && isExtended) {
      this._retract(x, y, z);
    }
  }

  _extend(x, y, z) {
    const facing = this.getFacing(x, y, z);
    const dir = FACING_OFFSETS[facing];
    if (!dir) return;

    // Collect blocks to push
    const blocksToPush = [];
    for (let i = 1; i <= MAX_PUSH_BLOCKS + 1; i++) {
      const bx = x + dir.x * i;
      const by = y + dir.y * i;
      const bz = z + dir.z * i;
      const block = this.world.getBlock(bx, by, bz);

      if (block === BlockId.AIR || block === BlockId.WATER) break; // space found
      if (IMMOVABLE.has(block)) return; // can't push immovable
      if (i > MAX_PUSH_BLOCKS) return; // too many blocks

      blocksToPush.push({ x: bx, y: by, z: bz, block });
    }

    // Push blocks (from far to near to avoid overwriting)
    for (let i = blocksToPush.length - 1; i >= 0; i--) {
      const b = blocksToPush[i];
      this.world.setBlock(b.x + dir.x, b.y + dir.y, b.z + dir.z, b.block);
    }

    // Place piston head
    const hx = x + dir.x;
    const hy = y + dir.y;
    const hz = z + dir.z;
    this.world.setBlock(hx, hy, hz, BlockId.PISTON_HEAD);

    this._extendedPistons.add(`${x},${y},${z}`);
  }

  _retract(x, y, z) {
    const facing = this.getFacing(x, y, z);
    const dir = FACING_OFFSETS[facing];
    if (!dir) return;

    const te = this.tileEntities.data.get(`${x},${y},${z}`);
    const isSticky = te && te.sticky;

    // Remove piston head
    const hx = x + dir.x;
    const hy = y + dir.y;
    const hz = z + dir.z;
    if (this.world.getBlock(hx, hy, hz) === BlockId.PISTON_HEAD) {
      this.world.setBlock(hx, hy, hz, BlockId.AIR);
    }

    // Sticky piston: pull block back
    if (isSticky) {
      const px = x + dir.x * 2;
      const py = y + dir.y * 2;
      const pz = z + dir.z * 2;
      const pullBlock = this.world.getBlock(px, py, pz);
      if (pullBlock !== BlockId.AIR && pullBlock !== BlockId.WATER && !IMMOVABLE.has(pullBlock)) {
        this.world.setBlock(hx, hy, hz, pullBlock);
        this.world.setBlock(px, py, pz, BlockId.AIR);
      }
    }

    this._extendedPistons.delete(`${x},${y},${z}`);
  }

  /**
   * Check if piston at position is extended.
   */
  isExtended(x, y, z) {
    return this._extendedPistons.has(`${x},${y},${z}`);
  }

  serialize() {
    return { extended: Array.from(this._extendedPistons) };
  }

  deserialize(data) {
    if (data && data.extended) {
      this._extendedPistons = new Set(data.extended);
    }
  }
}
